import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { AuthShell, Field } from "@/pages/Login";

export default function Register() {
    const [firstName, setFirstName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();

    async function onSubmit(e) {
        e.preventDefault();
        if (password.length < 8) {
            toast.error("Le mot de passe doit contenir au moins 8 caractères.");
            return;
        }
        setLoading(true);
        try {
            const user = await register(firstName.trim(), email.trim(), password);
            toast.success(`Bienvenue, ${user.first_name}.`);
            navigate("/onboarding", { replace: true });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }

    return (
        <AuthShell testId="register-page">
            <Link to="/" className="inline-flex items-center gap-1 text-inkSoft hover:text-ink text-sm mb-8" data-testid="back-home">
                <ArrowLeft className="w-4 h-4" /> Accueil
            </Link>
            <h1 className="font-serif text-3xl md:text-4xl tracking-tight" data-testid="register-title">
                Créez votre compte.
            </h1>
            <p className="mt-2 text-sm text-inkSoft">
                Trois minutes suffisent pour configurer votre parcours.
            </p>

            <form className="mt-8 space-y-5" onSubmit={onSubmit} data-testid="register-form">
                <Field label="Prénom ou pseudo" id="first_name">
                    <input
                        id="first_name"
                        type="text"
                        required
                        autoComplete="given-name"
                        data-testid="register-first-name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="w-full h-11 rounded-xl border border-lexiBorder bg-paper px-4 focus:outline-none focus:ring-2 focus:ring-terracotta"
                    />
                </Field>
                <Field label="Email" id="reg-email">
                    <input
                        id="reg-email"
                        type="email"
                        required
                        autoComplete="email"
                        data-testid="register-email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full h-11 rounded-xl border border-lexiBorder bg-paper px-4 focus:outline-none focus:ring-2 focus:ring-terracotta"
                    />
                </Field>
                <Field label="Mot de passe (8+ caractères)" id="reg-password">
                    <input
                        id="reg-password"
                        type="password"
                        required
                        autoComplete="new-password"
                        minLength={8}
                        data-testid="register-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full h-11 rounded-xl border border-lexiBorder bg-paper px-4 focus:outline-none focus:ring-2 focus:ring-terracotta"
                    />
                </Field>

                <button
                    type="submit"
                    disabled={loading}
                    data-testid="register-submit"
                    className="w-full h-11 rounded-full bg-terracotta hover:bg-terracottaDark text-white text-sm font-medium disabled:opacity-60 active:scale-[0.98] transition-transform inline-flex items-center justify-center gap-2"
                >
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    Créer mon compte
                </button>
            </form>

            <p className="mt-8 text-sm text-inkSoft">
                Déjà inscrit ?{" "}
                <Link to="/login" className="text-terracotta hover:underline" data-testid="link-login">
                    Se connecter
                </Link>
            </p>
        </AuthShell>
    );
}
